import { AnimatePresence, motion } from "motion/react";
import { Building2, LogOut, Menu as MenuIcon, ReceiptText, ScrollText, X } from "lucide-react";
import { Suspense, useState, type ReactNode } from "react";
import { CenteredLoading } from "../ui/components/States";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { cn } from "../lib/cn";
import { ThemeSwitch } from "../ui/components/ThemeSwitch";
import { BrandMark } from "../pages/auth/BrandMark";
import { initials } from "../lib/format";

const NAV: { to: string; label: string; icon: ReactNode }[] = [
  { to: "/admin/tenants", label: "Tenants", icon: <Building2 /> },
  { to: "/admin/plans", label: "Plans", icon: <ReceiptText /> },
  { to: "/admin/audit-log", label: "Audit Log", icon: <ScrollText /> },
];

/** Superadmin shell (§6.3): dark navy rail "BaraPOS Admin", light content. Below 1024 px the
 * rail becomes a drawer behind a menu button. */
export function AdminShell() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const [path, setPath] = useState(loc.pathname);
  if (path !== loc.pathname) {
    setPath(loc.pathname);
    setOpen(false);
  }
  return (
    <div className="flex h-dvh">
      <aside className="hidden w-[248px] shrink-0 lg:flex">
        <Rail />
      </aside>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <motion.div className="absolute inset-0 bg-neutral-900/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} aria-hidden="true" />
            <motion.aside role="dialog" aria-modal="true" aria-label="Navigation" className="absolute inset-y-0 left-0 flex w-[260px]" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", stiffness: 460, damping: 40 }}>
              <button type="button" onClick={() => setOpen(false)} className="absolute right-2 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-[9px] text-white/70 hover:bg-white/10" aria-label="Close navigation">
                <X className="h-4 w-4" />
              </button>
              <Rail />
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="glass flex h-[56px] shrink-0 items-center gap-3 px-4 lg:hidden">
          <button type="button" onClick={() => setOpen(true)} className="btn btn-ghost btn-icon btn-sm" aria-label="Open navigation">
            <MenuIcon />
          </button>
          <BrandMark size={30} sub="Admin" />
        </header>
        <main id="main" className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8 lg:py-7">
          <Suspense fallback={<CenteredLoading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function Rail() {
  const { user, logout } = useAuth();
  return (
    <nav aria-label="Main" className="relative flex w-full flex-col overflow-hidden bg-[linear-gradient(180deg,var(--color-navy)_0%,var(--color-navy-2)_100%)] text-white shadow-[inset_-1px_0_0_rgb(255_255_255/0.06)]">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgb(255_255_255/.7)_1px,transparent_1px),linear-gradient(90deg,rgb(255_255_255/.7)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:linear-gradient(180deg,black,transparent_60%)]" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-20 h-[280px] w-[280px] rounded-full bg-[linear-gradient(135deg,var(--hero-from),var(--hero-to))] opacity-30 blur-[80px]" />
      <div className="relative flex h-[72px] items-center px-5">
        <BrandMark size={36} onDark sub="Admin console" />
      </div>
      <div className="relative px-5 pb-2 pt-3 text-2xs font-bold uppercase tracking-[0.16em] text-white/50">Platform</div>
      <ul className="relative flex-1 space-y-1 px-3">
        {NAV.map((n) => (
          <li key={n.to}>
            <NavLink to={n.to} className={({ isActive }) => cn("group relative flex h-11 items-center gap-3 rounded-[10px] px-3 text-base font-semibold transition-colors duration-150 [&_svg]:h-[18px] [&_svg]:w-[18px]", isActive ? "bg-white/12 text-white shadow-[inset_0_0_0_1px_rgb(255_255_255/0.1)]" : "text-white/70 hover:bg-white/6 hover:text-white")}>
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute -left-3 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-white shadow-[0_0_12px_var(--hero-glow)]" aria-hidden="true" />}
                  <span className={cn("inline-flex h-7 w-7 items-center justify-center rounded-[8px] transition-colors", isActive ? "bg-[linear-gradient(135deg,var(--hero-from),var(--hero-to))] text-white shadow-[0_6px_16px_-6px_var(--hero-glow)]" : "bg-white/6 text-white/75 group-hover:text-white")}>{n.icon}</span>
                  {n.label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
      <div className="relative px-3 pb-3">
        <div className="mb-1.5 px-2 text-2xs font-bold uppercase tracking-[0.16em] text-white/50">Theme</div>
        <ThemeSwitch onRail className="w-full [&>button]:flex-1" />
      </div>
      <div className="relative mx-3 mb-3 flex items-center gap-3 rounded-[12px] bg-white/6 p-3 ring-1 ring-white/8">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,var(--hero-from),var(--hero-to))] text-xs font-bold" aria-hidden="true">
          {initials(user?.fullName ?? "Platform Administrator")}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold">{user?.fullName ?? "Platform Administrator"}</div>
          <div className="text-2xs text-white/60">Platform superadmin</div>
        </div>
        <button type="button" onClick={logout} className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] text-white/70 transition-colors hover:bg-white/10 hover:text-white" aria-label="Log out" title="Log out">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
}
